import Reveal from "@/components/site/Reveal";
import PageHero from "@/components/site/PageHero";
import LeadForm from "@/components/site/LeadForm";
import Seo, { ORG_SCHEMA, breadcrumbSchema } from "@/components/site/Seo";
import { BRAND } from "@/lib/constants";
import { Mail, Phone, MapPin, Calendar, ArrowRight } from "lucide-react";

export default function Contact() {
  return (
    <div className="bg-white">
      <Seo
        title="Contact — British UAE Consultancy"
        description="Book a private consultation with Velor Advisory — British-owned UAE consultancy for Golden Visa, residency, business setup and relocation."
        keywords="Velor Advisory contact, book UAE consultation, British consultancy Dubai contact, UAE Golden Visa consultation, Dubai business setup enquiry"
        focusKeyword="UAE consultancy contact"
        path="/contact"
        schema={[
          ORG_SCHEMA,
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "@id": "https://velor.ae/contact#contactpage",
            url: "https://velor.ae/contact",
            name: "Contact Velor Advisory",
            description:
              "Book a private consultation with Velor Advisory for Golden Visa, business setup and relocation guidance.",
            mainEntity: { "@id": "https://velor.ae/#organization" },
            inLanguage: "en-GB",
          },
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      <PageHero
        overline="Get in touch"
        title="Begin a private conversation."
        subtitle="A 30-minute confidential consultation, with a senior advisor. No commitment, no pressure."
        image="https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?auto=format&fit=crop&w=1650&q=75"
        imageAlt="Senior advisors in a private consultation — book a meeting with Velor Advisory in Dubai"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-black/45">Reach Velor</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">Three ways to start.</h2>

            <div className="mt-12 space-y-10">
              <div className="border-t border-black/10 pt-8">
                <div className="flex items-center gap-3 text-[11px] tracking-overline uppercase text-black/45"><Calendar size={14}/> Schedule</div>
                <div className="font-serif text-2xl mt-3">Book a free 30-min consultation</div>
                <a href={BRAND.calendly} target="_blank" rel="noreferrer" data-testid="contact-page-calendly" className="inline-flex items-center gap-2 mt-4 text-[12px] tracking-overline uppercase border-b border-black pb-1 hover:opacity-60">Open Calendly <ArrowRight size={12}/></a>
              </div>
              <div className="border-t border-black/10 pt-8">
                <div className="flex items-center gap-3 text-[11px] tracking-overline uppercase text-black/45"><Phone size={14}/> WhatsApp</div>
                <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="contact-page-whatsapp" className="font-serif text-2xl mt-3 block hover:opacity-70">{BRAND.whatsapp}</a>
                <div className="mt-2 text-sm text-black/55 font-light">Replies typically within minutes during UAE business hours.</div>
              </div>
              <div className="border-t border-black/10 pt-8">
                <div className="flex items-center gap-3 text-[11px] tracking-overline uppercase text-black/45"><Mail size={14}/> Email</div>
                <a href={`mailto:${BRAND.email}`} data-testid="contact-page-email" className="font-serif text-2xl mt-3 block hover:opacity-70">{BRAND.email}</a>
              </div>
              <div className="border-t border-black/10 pt-8">
                <div className="flex items-center gap-3 text-[11px] tracking-overline uppercase text-black/45"><MapPin size={14}/> Office</div>
                <div className="font-serif text-2xl mt-3">Dubai, United Arab Emirates</div>
                <div className="mt-2 text-sm text-black/55 font-light">By appointment only.</div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120} className="md:col-span-7">
            <LeadForm testid="contact-page-lead-form" source="contact-page" />
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0a0a0a] text-white py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h3 className="font-serif text-3xl md:text-4xl max-w-xl">Prefer to schedule directly?</h3>
          <a href={BRAND.calendly} target="_blank" rel="noreferrer" data-testid="contact-page-bottom-calendly" className="inline-flex items-center gap-3 px-8 py-5 bg-white text-black text-[12px] tracking-overline uppercase hover:bg-white/85 transition">Open Calendly <ArrowRight size={14}/></a>
        </div>
      </section>
    </div>
  );
}
